"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import "./pillCounterTool.css";

import {
  appendPillCounterLog,
  getPillCount,
  getLastPillCounterLog,
  getPillCounterAccess,
  incrementPillCount,
  PILL_PRO_MAX_USES,
  PILL_TRIAL_MAX_USES,
} from "@/lib/pillCounterQuota";
import { PillCounter } from "@/components/pill-counter/PillCounter";
import { PillDotOverlay } from "@/components/pill-counter/PillDotOverlay";
import LivePillCounter from "@/components/pill-counter/LivePillCounter";
import AppErrorBoundary from "@/components/errors/AppErrorBoundary";
import UpgradePrompt from "@/components/upgrade/UpgradePrompt";
import type { PillCounterScanSuccess } from "@/lib/pill-counter/api-types";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { breadcrumbs } from "@/lib/logger/sentryBreadcrumb";
import { PLANS, normalizeRuntimePlan, planSupports } from "@/lib/plans/planConfig";
import { safeTrack } from "@/lib/safeTrack";
import { supabase } from "@/lib/supabaseClient";

function captureFromVideo(video: HTMLVideoElement | null) {
  if (!video || video.readyState < 2) return null;
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return null;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.88) };
}

function formatJustNow(ts: number) {
  try {
    return new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
}

export function PillCounterToolView() {
  const router = useRouter();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const countInputRef = useRef<HTMLInputElement | null>(null);
  const previewImgRef = useRef<HTMLImageElement | null>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [plan, setPlan] = useState<string>("free");
  const [subStatus, setSubStatus] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [ui, setUi] = useState<"live" | "review" | "success">("live");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [countInput, setCountInput] = useState("");
  const [resultErr, setResultErr] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);
  const [lastLoggedUnits, setLastLoggedUnits] = useState<number | null>(null);
  const [loggedAt, setLoggedAt] = useState<number | null>(null);
  const [lastDeviceLog, setLastDeviceLog] = useState<ReturnType<typeof getLastPillCounterLog>>(null);
  const [pillCountsUsed, setPillCountsUsed] = useState(0);
  const [aiOverlayDims, setAiOverlayDims] = useState({ w: 0, h: 0 });
  const [aiScanResult, setAiScanResult] = useState<PillCounterScanSuccess | null>(null);
  const [mode, setMode] = useState<"photo" | "live">("photo");

  const refreshSession = useCallback(() => {
    if (!supabase) {
      setAccessToken(null);
      setAuthLoading(false);
      return;
    }
    void (async () => {
      const { data: u } = await supabase.auth.getUser();
      setUserId(u.user?.id ?? null);
      setEmail(u.user?.email ?? null);
      setCreatedAt(u.user?.created_at ?? null);
      const sess = (await supabase.auth.getSession()).data.session;
      const token = sess?.access_token ?? null;
      setAccessToken(token);
      if (!token) {
        setPlan("free");
        setSubStatus(null);
        setAuthLoading(false);
        return;
      }
      try {
        const res = await fetch("/api/billing/status", { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok && data) {
          setPlan(typeof data.plan === "string" ? data.plan : "free");
          setSubStatus(typeof data.status === "string" ? data.status : null);
        }
      } catch {
        setPlan("free");
        setSubStatus(null);
      }
      setAuthLoading(false);
    })();
  }, []);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      refreshSession();
    });
    if (!supabase) {
      return () => window.cancelAnimationFrame(id);
    }
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      refreshSession();
    });
    return () => {
      window.cancelAnimationFrame(id);
      sub.subscription.unsubscribe();
    };
  }, [refreshSession]);

  const access = getPillCounterAccess({
    userId,
    paidPlan: plan,
    subscriptionStatus: subStatus,
    userCreatedAtIso: createdAt,
  });

  const canScan = Boolean(userId && access.ok);
  const normalizedPlan = normalizeRuntimePlan(plan, Boolean(userId));
  const shouldShowFreeNudge = normalizedPlan === PLANS.FREE && pillCountsUsed >= 3;
  const liveModeEnabled = planSupports(normalizedPlan, "liveCameraEnabled");

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!canScan || ui !== "live") return;
    let cancelled = false;
    void (async () => {
      setCameraReady(false);
      setCameraError(false);
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) setCameraError(true);
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          await v.play().catch(() => {});
        }
        if (!cancelled) setCameraReady(true);
      } catch {
        if (!cancelled) setCameraError(true);
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [canScan, ui, stopStream]);

  useEffect(() => {
    if (ui !== "live" || !canScan) return;
    const id = window.requestAnimationFrame(() => {
      setLastDeviceLog(getLastPillCounterLog());
      setPillCountsUsed(getPillCount());
    });
    return () => window.cancelAnimationFrame(id);
  }, [ui, canScan]);

  const syncAiOverlayDims = useCallback(() => {
    const el = previewImgRef.current;
    if (!el) return;
    setAiOverlayDims({ w: el.clientWidth, h: el.clientHeight });
  }, []);

  useEffect(() => {
    if (ui !== "review" || !previewUrl) return;
    const el = previewImgRef.current;
    if (!el) return;
    syncAiOverlayDims();
    const ro = new ResizeObserver(() => syncAiOverlayDims());
    ro.observe(el);
    const onWin = () => syncAiOverlayDims();
    window.addEventListener("resize", onWin);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onWin);
    };
  }, [ui, previewUrl, syncAiOverlayDims]);

  useEffect(() => {
    if (ui !== "review") return;
    const t = window.requestAnimationFrame(() => {
      countInputRef.current?.focus();
      countInputRef.current?.select?.();
    });
    return () => window.cancelAnimationFrame(t);
  }, [ui]);

  const handleShutter = useCallback(() => {
    if (!cameraReady) return;
    const shot = captureFromVideo(videoRef.current);
    if (!shot) return;
    setFlash(true);
    window.setTimeout(() => setFlash(false), 70);
    setPreviewUrl(shot.dataUrl);
    setUi("review");
    setResultErr(null);
    setCountInput("");
    setAiScanResult(null);
    setAiOverlayDims({ w: 0, h: 0 });
    stopStream();
  }, [cameraReady, stopStream]);

  const onPickFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== "string") return;
        setPreviewUrl(dataUrl);
        setUi("review");
        setResultErr(null);
        setCountInput("");
        setAiScanResult(null);
        setAiOverlayDims({ w: 0, h: 0 });
        stopStream();
      };
      reader.readAsDataURL(f);
    },
    [stopStream],
  );

  const goLive = useCallback(() => {
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCountInput("");
    setResultErr(null);
    setLastLoggedUnits(null);
    setLoggedAt(null);
    setAiScanResult(null);
    setAiOverlayDims({ w: 0, h: 0 });
    setMode("photo");
    setUi("live");
  }, [previewUrl]);

  function handleLogCount() {
    if (!access.ok) return;
    const n = Number.parseInt(countInput.replace(/\D/g, ""), 10);
    if (!Number.isFinite(n) || n < 1) {
      setResultErr("Enter a positive whole number of units.");
      return;
    }
    incrementPillCount();
    setPillCountsUsed(getPillCount());
    appendPillCounterLog(n);
    const at = Date.now();
    setLastLoggedUnits(n);
    setLoggedAt(at);
    setResultErr(null);
    setUi("success");
    breadcrumbs.pillCountConfirmed(n, aiScanResult?.pill_count ?? n);
    safeTrack(
      async () => {
        await fetch("/api/analytics/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: EVENTS.PILL_COUNT_CONFIRMED,
            properties: {
              confirmedCount: n,
              aiCount: aiScanResult?.pill_count ?? null,
              delta: aiScanResult?.pill_count != null ? n - aiScanResult.pill_count : null,
              scanId: aiScanResult?.scan_id ?? null,
            },
          }),
          keepalive: true,
        });
      },
      "pill_count_confirmed",
    );
    if (accessToken && aiScanResult?.scan_id != null) {
      void fetch("/api/learning/pill-count-feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          scanId: aiScanResult.scan_id,
          humanCount: n,
          aiCount: aiScanResult.pill_count ?? null,
        }),
        keepalive: true,
      }).catch(() => {});
    }
  }

  const allowanceLine =
    access.ok && access.kind === "premium" ? (
      <span>
        <strong style={{ color: "#7dd3fc" }}>Owner</strong> — unlimited counts on this device.
      </span>
    ) : access.ok ? (
      <span>
        <strong style={{ color: "#7dd3fc" }}>{access.remaining}</strong> of {access.max} counts left (this device).
      </span>
    ) : null;

  return (
    <div className="ed-pill-tool batch-scan-ui">
      <input ref={fileRef} type="file" accept="image/*" className="ed-visually-hidden" tabIndex={-1} onChange={onPickFile} />

      <header className="ed-pill-tool__top">
        <div className="ed-pill-tool__top-left">
          <button type="button" className="ed-pill-tool__back" onClick={() => router.push("/tools")}>
            ← Tools
          </button>
          <div className="ed-pill-tool__title-block">
            <p className="ed-pill-tool__title">Pill counter</p>
            <p className="ed-pill-tool__tagline">The count that never fails the record</p>
          </div>
        </div>
        <Link href="/dashboard" className="ed-pill-tool__desk">
          Desk
        </Link>
      </header>

      <div className="ed-pill-tool__body">
        {flash ? <div className="ed-pill-tool__flash" aria-hidden /> : null}

        {authLoading ? <div className="ed-pill-tool__loading">Loading…</div> : null}

        {!authLoading && !userId ? (
          <div className="ed-pill-tool__gate" role="dialog" aria-labelledby="pill-gate-signin-title">
            <div className="ed-pill-tool__gate-card">
              <h2 id="pill-gate-signin-title">Shelf counts, signed in</h2>
              <p>
                ExpiryDesk ties camera proof to your account so allowances stay fair and your desk stays audit-ready. Sign in once, then count
                with confidence.
              </p>
              <div className="ed-pill-tool__gate-actions">
                <Link href="/dashboard" className="ed-pill-tool__cta-primary">
                  Open dashboard to sign in
                </Link>
                <Link href="/pricing" className="ed-pill-tool__cta-secondary">
                  View plans (Manager &amp; Owner)
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {!authLoading && userId && !access.ok ? (
          <div className="ed-pill-tool__gate" role="dialog" aria-labelledby="pill-gate-lock-title">
            <div className="ed-pill-tool__gate-card">
              <h2 id="pill-gate-lock-title">Unlock the full counter</h2>
              <p className="ed-pill-tool__gate-err">{access.reason}</p>
              <div className="ed-pill-tool__gate-actions">
                <Link href="/manage-subscription" className="ed-pill-tool__cta-primary">
                  Manage subscription
                </Link>
                <Link href="/pricing" className="ed-pill-tool__cta-secondary">
                  Compare Manager vs Owner
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {canScan && ui === "live" ? (
          <>
            <video ref={videoRef} className="ed-pill-tool__camera" playsInline muted autoPlay />
            <div className="ed-pill-tool__camera-scrim" aria-hidden />
            <div className="ed-pill-tool__hud">
              {allowanceLine ? <p className="ed-pill-tool__allowance">{allowanceLine}</p> : null}
              {lastDeviceLog ? (
                <p className="ed-pill-tool__hint" style={{ fontSize: "0.72rem", color: "rgba(167, 243, 208, 0.85)" }}>
                  Last on this device: <strong>{lastDeviceLog.units}</strong> units · {formatJustNow(lastDeviceLog.at)}
                </p>
              ) : null}
              <p className="ed-pill-tool__hint">
                Frame tablets or a blister pack. Capture a clear shot, enter the units you verified, and log — one tap keeps the paper trail
                honest. Trial: {PILL_TRIAL_MAX_USES} · Manager: {PILL_PRO_MAX_USES} · Owner: unlimited.
              </p>
              {!cameraError ? (
                <button
                  type="button"
                  onClick={handleShutter}
                  disabled={!cameraReady}
                  className="ed-scan-shutter"
                  aria-label="Capture frame for your count"
                  style={{ width: 88, height: 88, opacity: cameraReady ? 1 : 0.45 }}
                >
                  <span className="ed-scan-shutter-inner" />
                </button>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} className="ed-scan-fallback-primary">
                  Choose photo
                </button>
              )}
              {!cameraError ? (
                <button type="button" onClick={() => fileRef.current?.click()} className="ed-scan-ghost" style={{ fontSize: "0.8rem" }}>
                  Upload photo instead
                </button>
              ) : null}
            </div>
          </>
        ) : null}

        {canScan && ui === "review" && previewUrl ? (
          <div className="ed-pill-tool__panel">
            <div className="ed-pill-tool__preview">
              {/* eslint-disable-next-line @next/next/no-img-element -- user-captured photo */}
              <img
                ref={previewImgRef}
                src={previewUrl}
                alt="Captured frame for your shelf count"
                onLoad={syncAiOverlayDims}
              />
              {aiScanResult && aiOverlayDims.w > 0 && aiOverlayDims.h > 0 ? (
                <PillDotOverlay
                  positions={aiScanResult.pill_positions}
                  width={aiOverlayDims.w}
                  height={aiOverlayDims.h}
                />
              ) : null}
            </div>
            <div className="ed-pill-tool__sheet">
              <div
                style={{
                  display: "flex",
                  borderRadius: 10,
                  border: "1px solid rgba(148, 163, 184, 0.35)",
                  overflow: "hidden",
                  marginBottom: "0.85rem",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                }}
              >
                <button
                  type="button"
                  onClick={() => setMode("photo")}
                  style={{
                    flex: 1,
                    padding: "0.5rem 0.6rem",
                    border: "none",
                    cursor: "pointer",
                    background: mode === "photo" ? "#2563eb" : "#fff",
                    color: mode === "photo" ? "#fff" : "#64748b",
                  }}
                >
                  📷 Photo
                </button>
                <button
                  type="button"
                  onClick={() => setMode("live")}
                  style={{
                    flex: 1,
                    padding: "0.5rem 0.6rem",
                    border: "none",
                    cursor: "pointer",
                    background: mode === "live" ? "#2563eb" : "#fff",
                    color: mode === "live" ? "#fff" : "#64748b",
                  }}
                >
                  ⚡ Live
                </button>
              </div>

              {mode === "live" && !liveModeEnabled ? (
                <UpgradePrompt
                  feature="liveCameraEnabled"
                  triggerLabel="Live Camera Detection"
                  benefitStatement="See dots appear on pills in real time as you point your camera. Instant visual feedback — no upload needed."
                  requiredPlan={PLANS.PRO}
                  variant="inline"
                  onDismiss={() => setMode("photo")}
                />
              ) : null}

              {mode === "live" && liveModeEnabled ? (
                <LivePillCounter
                  medicationName={aiScanResult?.detected_medicine_name ?? undefined}
                  onCountCaptured={(count) => {
                    if (count === -1) {
                      setMode("photo");
                      return;
                    }
                    setCountInput(String(count));
                    setMode("photo");
                  }}
                />
              ) : null}

              {mode === "photo" ? (
              <AppErrorBoundary context="pill-counter">
                <PillCounter
                  previewUrl={previewUrl}
                  accessToken={accessToken}
                  disabled={!access.ok}
                  onAiSuccess={setAiScanResult}
                  onSuggestedCount={(n) => setCountInput(String(n))}
                />
              </AppErrorBoundary>
              ) : null}
              <h3>Confirm units</h3>
              <p>You captured proof on-device. Enter the unit count you verified from this frame — that value is what we log against your plan.</p>
              <div className="ed-pill-tool__row">
                <input
                  ref={countInputRef}
                  value={countInput}
                  onChange={(e) => {
                    setCountInput(e.target.value);
                    setResultErr(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleLogCount();
                  }}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Units (e.g. 24)"
                  className="ed-pill-tool__input"
                  aria-label="Number of units to log"
                />
                <button type="button" className="ed-scan-primary" style={{ padding: "11px 18px", fontWeight: 700 }} onClick={handleLogCount}>
                  Log count
                </button>
              </div>
              {resultErr ? <p className="ed-pill-tool__err">{resultErr}</p> : null}
              <div className="ed-pill-tool__footer-row">
                <button type="button" onClick={goLive} className="ed-scan-ghost">
                  ← Retake
                </button>
                {email ? <span className="ed-pill-tool__meta">{email}</span> : null}
              </div>
            </div>
          </div>
        ) : null}

        {canScan && ui === "success" && lastLoggedUnits != null && loggedAt != null ? (
          <div className="ed-pill-tool__success" role="status" aria-live="polite">
            <div className="ed-pill-tool__success-card">
              <div className="ed-pill-tool__success-icon" aria-hidden>
                ✓
              </div>
              <h2>Logged {lastLoggedUnits} units</h2>
              <p className="ed-pill-tool__success-sub">
                Saved {formatJustNow(loggedAt)}. Your allowance was updated on this device — bring the same discipline to dated stock on the desk.
              </p>
              <p className="ed-pill-tool__success-brand">ExpiryDesk — the count that never fails the record</p>
              <div className="ed-pill-tool__success-actions">
                <Link href="/dashboard" className="ed-pill-tool__cta-primary">
                  Open desk &amp; add inventory
                </Link>
                <button type="button" className="ed-pill-tool__cta-ghost" onClick={goLive}>
                  Count another pack
                </button>
                <Link href="/pricing" className="ed-pill-tool__cta-ghost" style={{ textAlign: "center", borderStyle: "dashed" }}>
                  Upgrade for unlimited counts (Owner)
                </Link>
              </div>
              {shouldShowFreeNudge ? (
                <div style={{ marginTop: "0.65rem", width: "100%" }}>
                  <UpgradePrompt
                    feature="pillCountsPerMonth"
                    triggerLabel={`You've used ${pillCountsUsed} of ${PILL_TRIAL_MAX_USES} free pill counts this month`}
                    benefitStatement="Upgrade to Pro for 100 counts per month."
                    requiredPlan={PLANS.PRO}
                    onDismiss={() => {}}
                    variant="inline"
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
